/* eslint-disable no-underscore-dangle */
import { useApolloClient } from '@apollo/client';
import {
  AppState,
  Auth0Provider,
  CacheLocation,
  useAuth0,
  User,
} from '@auth0/auth0-react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import * as Sentry from '@sentry/react';
import { RouterProvider } from '@tanstack/react-router';
import Ajv from 'ajv';
import {
  useEffect,
  // StrictMode,
  useState,
} from 'react';
import ReactDOM from 'react-dom/client';

// css for root component
import './components/App.css';

// relative imports
import { captureSentryGenericError } from './components/error-handling/sentry';
import AuthorizedApolloProvider from './components/providers/AuthorizedApolloProvider';
import { hasVerifiedEmailClaim } from './components/providers/LedgibleAuthProvider';
import LoadingPage from './components/shared/page-components/LoadingPage';
import { clearStaleOrgContext } from './components/shared/authentication/orgResolution';
import { getAuth0DefaultClientOptions } from './components/shared/utilities/appUtilities';
import { persistor } from './data/apollo/cache/cache';
import { getAppConfig } from './data/app-config/configs/configUtilities';
import appConfigSchema from './data/app-config/configs/validation/app-config-schema.json';
import router from './router';

// Sentry init
// Trace headers belong on requests to the API, which is a different host from the
// app and differs per environment, so derive the target from the API URL itself
// rather than hardcoding one. Strings are matched as a substring of the request URL.
const apiUrl = import.meta.env.REACT_APP_API_URL;

Sentry.init({
  dsn: import.meta.env.REACT_APP_SENTRY_DSN,
  // denyUrls: ['localhost'],
  environment: import.meta.env.REACT_APP_SENTRY_ENVIRONMENT || 'local',
  // enables automatic instrumentation
  integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
  // can adjust this value, or use tracesSampler
  tracesSampleRate: 0.25,
  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  tracePropagationTargets: apiUrl ? [apiUrl] : [],
  // set enabled based on env
  enabled: !window.Cypress && import.meta.env.MODE !== 'ci',
});

// Emotion cache for customization/override purposes like removing annoying SSR warning
const emotionCache = createCache({
  key: 'css',
  prepend: true,
});

emotionCache.compat = true;

// Register things for typesafety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
  interface StaticDataRouteOption {
    hideAccountMenu?: boolean;
    hideHeader?: boolean;
    hideSecondaryMenu?: boolean;
    hideUpdateButton?: boolean;
    accountOptional?: boolean;
    configPageId?: string;
    isClientRoute?: boolean;
    meta?: object;
  }
}

declare global {
  interface Window {
    appConfig: any;
    appTheme: any;
    appConfigPromise: Promise<void>;
  }
}

const ajv = new Ajv();
const validateApp = ajv.compile(appConfigSchema);

const onRedirectCallback = (appState: AppState = {}, user: User = {}) => {
  const { returnTo, tmpUserToken, codeInviteToken } = appState;
  const searchParams = {} as Record<string, string>;

  if (tmpUserToken) {
    searchParams.tmpUserToken = tmpUserToken;
  }
  if (codeInviteToken) {
    searchParams.codeInviteToken = codeInviteToken;
  }

  if (user.email && !hasVerifiedEmailClaim(user)) {
    router.navigate({
      replace: true,
      to: '/verify-email',
      search: searchParams as unknown as true,
    });
  } else {
    router.navigate({
      replace: true,
      to: returnTo !== '/auth-callback' ? returnTo : '/',
      search: searchParams as unknown as true,
    });
  }
};

const auth0ClientOptions = {
  ...getAuth0DefaultClientOptions(),
  cacheLocation: 'localstorage' as CacheLocation,
  onRedirectCallback,
};

const App = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const auth0 = useAuth0();
  const apolloClient = useApolloClient();

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Wait for app config to be ready
        await window.appConfigPromise;

        /* Restore persisted cache, enable MSW intercept for testing purposes if needed */
        if (!window.Cypress) {
          await persistor.restore();
        }

        if (import.meta.env.REACT_APP_USE_MSW === 'true') {
          const { default: worker } = await import(
            `../test/mocks/msw/workers/browser`
          );
          const { activateStoredMocks } = await import(
            `../test/mocks/msw/mock-endpoint`
          );
          await worker.start({
            onUnhandledRequest: 'bypass',
          });
          activateStoredMocks();
        }

        // validate app config
        const valid = validateApp(getAppConfig());

        if (!valid) {
          throw new Error(
            `Error validating app config - ${JSON.stringify(validateApp.errors)}`,
          );
        }

        setIsInitialized(true);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error initializing app', e);
        captureSentryGenericError(e, {
          file: 'index.tsx',
          warn: 'Initialization error detected',
        });
      }
    };

    if (!!apolloClient && !isInitialized) {
      initializeApp();
    }
  }, [apolloClient, isInitialized]);

  // Clear a stale org cached by a failed login so it can't re-arm validation.
  useEffect(() => {
    if (auth0?.error) {
      clearStaleOrgContext();
    }
  }, [auth0?.error]);

  if (!isInitialized || !auth0 || auth0.isLoading) {
    return <LoadingPage loadingApplication />;
  }

  return (
    <RouterProvider
      router={router}
      context={{
        auth0,
        apolloClient,
      }}
    />
  );
};

const rootElement = document.getElementById('app')!;

// Store root in module-level variable to reuse it and prevent memory leaks
let root: ReturnType<typeof ReactDOM.createRoot> | null = null;

// React 18's createRoot() will throw if called on an element that already has a root
const existingRoot = (rootElement as any)._reactRootContainer;

if (existingRoot) {
  // Element already has a root - try to reuse it (development/HMR with persistent DOM)
  // Check if it's a valid root instance (has render method)
  if (typeof existingRoot.render === 'function') {
    root = existingRoot as ReturnType<typeof ReactDOM.createRoot>;
  } else {
    // Invalid root - unmount it before creating a new one
    try {
      if (typeof existingRoot.unmount === 'function') {
        existingRoot.unmount();
      }
    } catch {
      // Ignore errors - React will handle it when we create the new root
    }
    root = ReactDOM.createRoot(rootElement);
  }
} else {
  // No existing root - create a new one
  root = ReactDOM.createRoot(rootElement);
}

root.render(
  <CacheProvider value={emotionCache}>
    <Auth0Provider {...auth0ClientOptions}>
      <AuthorizedApolloProvider>
        <App />
      </AuthorizedApolloProvider>
    </Auth0Provider>
  </CacheProvider>,
);
